import camelCaseKeys from 'camelcase-keys'
import camelCase from 'lodash/camelCase'
import mapKeys from 'lodash/mapKeys'
import snakeCase from 'lodash/snakeCase'
import snakeCaseKeysExternal from 'snakecase-keys'

export const mapKeysToCamelCase = <T extends Record<string, any> | readonly any[]>(object: T) =>
  camelCaseKeys<T, { deep: true }>(object, { deep: true })

export const mapKeysToSnakeCase = <T extends Record<string, any> | readonly any[]>(object: T) =>
  snakeCaseKeysExternal<T, { deep: true }>(object, { deep: true })

export const transformArrayToCamelCase = (arr: Array<string>) => {
  const res: Array<string> = []
  arr.forEach(s => {
    res.push(camelCase(s))
  })
  return res
}

export const stringToSnakeCase = (str: string): string => snakeCase(str)

/**
 * @deprecated Use mapKeysToCamelCase instead.
 */
export const convertToCamelCase = <T extends Record<string, unknown>>(obj: T) =>
  mapKeys(obj, (_v, k) => camelCase(k))

/**
 * @deprecated Use mapKeysToCamelCase instead.
 */
export const convertArrayToCamelCase = <T extends Record<string, unknown>[]>(arr: T) =>
  arr.map(obj => mapKeys(obj, (_v, k) => camelCase(k)))

/**
 * @deprecated Use mapKeysToCamelCase instead.
 */
export const camelizeKeys = (data: unknown): unknown => {
  if (Array.isArray(data)) return data.map(v => camelizeKeys(v))
  if (data !== null && data?.constructor === Object) {
    return Object.keys(data).reduce(
      (result, key) => ({
        ...result,
        [camelCase(key)]: camelizeKeys((data as any)[key]),
      }),
      {}
    )
  }
  return data
}

function parseFalsyValues(val: unknown) {
  if (val === undefined || val === null || val === '') return null
  return val
}

/**
 * @deprecated Use mapKeysToSnakeCase instead.
 */
export const snakeCaseKeys = (data: unknown): unknown => {
  if (Array.isArray(data)) return data.map(v => snakeCaseKeys(v))

  if (data !== null && data?.constructor === Object) {
    return Object.keys(data).reduce(
      (result, key) => ({
        ...result,
        [snakeCase(key)]: snakeCaseKeys(parseFalsyValues((data as any)[key])),
      }),
      {}
    )
  }

  return data
}

export function convertAttributesToBoolean<T, K extends keyof T>(
  obj: T,
  objKeysToTransform: K[] | K
) {
  const newObj: any = { ...obj }
  const keys = Array.isArray(objKeysToTransform) ? objKeysToTransform : [objKeysToTransform]

  keys.forEach(key => {
    if (!(key in newObj)) throw new Error(`Object has no attribute '${String(key)}'`)
    newObj[key] = Boolean(obj[key])
  })

  return newObj as { [key in keyof T]: key extends K ? boolean : T[key] }
}

export type Camelized<T extends string> = T extends `${infer First}_${infer Rest}`
  ? `${Lowercase<First>}${Capitalize<Camelized<Rest>>}`
  : T extends `${infer First}-${infer Rest}`
    ? `${Lowercase<First>}${Capitalize<Camelized<Rest>>}`
    : T extends `${infer First} ${infer Rest}`
      ? `${Lowercase<First>}${Capitalize<Camelized<Rest>>}`
      : Lowercase<T>

export function toCamelCase<T extends string>(str: T) {
  return str
    .split(/[_-\s]+/)
    .map((word, index) => {
      const lowerWord = word.toLowerCase()
      if (index > 0 && lowerWord) {
        return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1)
      }
      return lowerWord
    })
    .filter(word => word)
    .join('') as Camelized<T>
}

export function toFirstUpperCase<T extends string>(str: T) {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}
